import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button,
  Grid,
  Icon,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  Collapse,
  Switch,
  CircularProgress
} from '@material-ui/core';
import {BackdropCircular, InfoTooltip} from 'components';
import {isCertificationOption} from 'helpers/certifications';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon
} from '@material-ui/icons';
import {
  getCustomOptionsPriceTypesApi,
  saveCustomOptionsPriceTypesApi,
  deleteCustomOptionValueApi,
  deleteCustomOptionApi
} from 'api';
import AddCustomOption from './addCustomOption';
import EditCustomOption from './editCustomOption';
import AddCustomOptionValue from './addCustomOptionValue';
import EditCustomOptionValue from './editCustomOptionValue';
import SwatchImagePreview from './swatchImagePreview';


const types = [
  {
    value: 'drop_down',
    label: 'Drop-down',
  },
  {
    value: 'radio',
    label: 'Radio Buttons',
  },
  {
    value: 'checkbox',
    label: 'Checkbox',
  },
  {
    value: 'multiple',
    label: 'Multiple Select',
  },
];
const CustomOptions = () => {
  const dispatch = useDispatch();
  const [openAddCustomOptionDialog, setOpenAddCustomOptionDialog] = useState(false);
  const [openEditCustomOptionDialog, setOpenEditCustomOptionDialog] = useState(false);
  const [expandedCustomOption, setExpandedCustomOption] = useState(null);
  const [editableOptionData, setEditableOptionData] = useState(null);
  const [editableOptionValueData, setEditableOptionValueData] = useState(null);
  const [openAddCustomOptionValueDialog, setOpenAddCustomOptionValueDialog] = useState(false);
  const [openEditCustomOptionValueDialog, setOpenEditCustomOptionValueDialog] = useState(false);
  const {
    editable_store_product: {sku},
    productCustomOptions: options,
    editCustomOptionLoading,
    addCustomOptionLoading,
    deleteCustomOptionLoading,
    deleteCustomOptionValueLoading,
    customOptionPriceTypes,
    saveCustomOptionPriceTypesLoading
  } = useSelector((state) => state.products);
  const loading = addCustomOptionLoading
    || deleteCustomOptionValueLoading
    || deleteCustomOptionLoading
    || editCustomOptionLoading
    || saveCustomOptionPriceTypesLoading;
  const optionsWithoutCertifications = options.filter((option) => !isCertificationOption(option));

  useEffect(() => {
    getCustomOptionsPriceTypesApi({dispatch, sku});
  }, []);

  const handleChangePriceType = (event) => {
    const {name, checked} = event.target;
    const priceTypes = {...customOptionPriceTypes, [name]: checked};
    saveCustomOptionsPriceTypesApi({dispatch, sku, priceTypes});
  };
  return (
    <Grid container spacing={3}>
      {!!optionsWithoutCertifications.length &&
        <Table
          size="medium"
          aria-label="customOptions"
          className="mx-4 mb-6"
        >
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Name</TableCell>
              <TableCell>Sort Order</TableCell>
              <TableCell>Option Type</TableCell>
              <TableCell>Required</TableCell>
              <TableCell>Is Swatch</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          {optionsWithoutCertifications.map((item, index) => {
            return (
              <TableBody key={`table-product-option-${index}`}>
                <TableRow margin={1}>
                  <TableCell align="center">
                    <IconButton
                      aria-label="expand row"
                      aria-expanded={expandedCustomOption === index}
                      size="small"
                      onClick={() => {
                        return expandedCustomOption === index ?
                          setExpandedCustomOption(null)
                          : setExpandedCustomOption(index);
                      }}
                    >
                      {expandedCustomOption === index ?
                        <KeyboardArrowUpIcon /> :
                        <KeyboardArrowDownIcon />
                      }
                    </IconButton>
                  </TableCell>
                  <TableCell>{item.title}</TableCell>
                  <TableCell>{item.sort_order}</TableCell>
                  <TableCell>
                    {types.map((option) => option.value === item.type ? option.label : false)}
                  </TableCell>
                  <TableCell>{item.is_require ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{item.is_swatch ? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    <IconButton
                      className="mr-2"
                      title="Add Custom Option Value"
                      onClick={() => {
                        setEditableOptionData(item);
                        setOpenAddCustomOptionValueDialog(true);
                      }}
                    >
                      <Icon className="text-gray">add_circle</Icon>
                    </IconButton>
                    <IconButton
                      className="mr-2"
                      title="Delete Custom Option"
                      onClick={() => deleteCustomOptionApi({ dispatch, id: item.option_id, sku: item.product_sku })}
                    >
                      <Icon className="text-muted">delete</Icon>
                    </IconButton>
                    <IconButton
                      className="mr-2"
                      title="Edit Custom Option"
                      onClick={() => {
                        setEditableOptionData(item);
                        setOpenEditCustomOptionDialog(true);
                      }}
                    >
                      <Icon className="text-gray">edit</Icon>
                    </IconButton>
                  </TableCell>
                </TableRow>
                {item.values &&
                <>
                  <TableRow size="small" className="bg-periwinkle-gray">
                    <TableCell className="py-0" colSpan={6}>
                      <Collapse
                        in={expandedCustomOption === index}
                        timeout="auto" unmountOnExit
                      >
                        <Box margin={1} >
                          <Typography variant="h6" gutterBottom component="div">
                            Custom Values:
                          </Typography>
                          <Table size="small" aria-label="customOptionValues">
                            <TableHead>
                              <TableRow>
                                {item.is_swatch &&
                                  <TableCell className="max-w-60">Image</TableCell>
                                }
                                <TableCell>Title</TableCell>
                                <TableCell>Sort Order</TableCell>
                                <TableCell>Price</TableCell>
                                <TableCell>Manage Stock</TableCell>
                                <TableCell>Quantity</TableCell>
                                <TableCell>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {item.values.map((itemValue, valueIndex) => (
                                <TableRow key={valueIndex}>
                                  {item.is_swatch &&
                                  <TableCell>
                                    {itemValue.images.length ?
                                      <SwatchImagePreview
                                        sku={item.product_sku}
                                        title={itemValue.title}
                                        sortOrder={itemValue.sort_order}
                                        imageUrl={itemValue.images[0].image_url}
                                        alt={itemValue.images[0].title_text}
                                        className="max-w-60 max-h-60"
                                      />
                                      :
                                      <Icon>image_not_supported_outlined</Icon>
                                    }
                                  </TableCell>
                                  }
                                  <TableCell component="th" scope="row">
                                    {itemValue.title}
                                  </TableCell>
                                  <TableCell>{itemValue.sort_order}</TableCell>
                                  <TableCell>
                                    {`$${(Math.round(itemValue.price * 100) / 100).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`}
                                  </TableCell>
                                  <TableCell>
                                    {itemValue.manage_stock ?
                                      <Icon className="text-primary">check_box</Icon>
                                      :
                                      <Icon className="text-muted">check_box_outline_blank_icon</Icon>
                                    }
                                  </TableCell>
                                  <TableCell>
                                    {itemValue.qty ?? '0'}
                                  </TableCell>
                                  <TableCell>
                                    <IconButton
                                      title="Delete"
                                      className="mr-2"
                                      style={{ display: item.values.length > 1 ? 'inline-flex' : 'none' }}
                                      onClick={() => {
                                        deleteCustomOptionValueApi({
                                          dispatch,
                                          sku: item.product_sku,
                                          option_type_id: itemValue.option_type_id
                                        });
                                      }}
                                    >
                                      <Icon className="text-muted">delete</Icon>
                                    </IconButton>
                                    <IconButton
                                      className="mr-2"
                                      title="Edit"
                                      onClick={() => {
                                        setEditableOptionValueData(itemValue);
                                        setEditableOptionData(item);
                                        setOpenEditCustomOptionValueDialog(true);
                                      }}
                                    >
                                      <Icon className="text-gray">edit</Icon>
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </>
                }
              </TableBody>
            );
          }
          )}
        </Table>
      }
      <Grid item xs={12}>
        <div className="flex items-center justify-between flex-wrap">
          <Button
            variant="contained"
            className="bg-light-primary mb-4"
            onClick={() => setOpenAddCustomOptionDialog(true)}
          >
            Add Custom Option
          </Button>
          {(!!Object.keys(customOptionPriceTypes).length && !saveCustomOptionPriceTypesLoading) ?
            <div className="flex items-center px-4">
              <div className="pr-5">
                <Typography variant="h5">
                  Absolute Pricing
                  <InfoTooltip title="Once this is enabled, it will display exact price of the selected option, ignoring the main price." />
                </Typography>
                <Switch
                  color="primary"
                  name="absolute_price"
                  checked={customOptionPriceTypes?.absolute_price}
                  onChange={(e) => handleChangePriceType(e)}
                /> Yes
              </div>
              <div className="pl-5">
                <Typography variant="h5">
                  Price Starts At
                  <InfoTooltip title='Once this is enabled, it will display a phrase "Price Starts At" on category view.' />
                </Typography>
                <Switch
                  color="primary"
                  name="start_pricing"
                  checked={customOptionPriceTypes?.start_pricing}
                  onChange={(e) => handleChangePriceType(e)}
                /> Yes
              </div>
            </div>
            :
            <CircularProgress className="m-2" />
          }
        </div>
      </Grid>
      {openAddCustomOptionDialog &&
        <AddCustomOption
          open={openAddCustomOptionDialog}
          onClose={setOpenAddCustomOptionDialog}
          options={optionsWithoutCertifications}
          product_sku={sku}
          types={types}
        />
      }
      {editableOptionData &&
        <EditCustomOption
          open={openEditCustomOptionDialog}
          onClose={setOpenEditCustomOptionDialog}
          option={editableOptionData}
          options={optionsWithoutCertifications}
          types={types}
        />
      }
      {editableOptionData &&
        <AddCustomOptionValue
          open={openAddCustomOptionValueDialog}
          onClose={setOpenAddCustomOptionValueDialog}
          option={editableOptionData}
        />
      }
      {editableOptionValueData &&
      <EditCustomOptionValue
        open={openEditCustomOptionValueDialog}
        onClose={setOpenEditCustomOptionValueDialog}
        value={editableOptionValueData}
        option={editableOptionData}
      />
      }
      <BackdropCircular
        loading={loading}
      />
    </Grid>
  );
};
export default CustomOptions;
